/**
 * Цвет зоны качества: зелёный — норма, жёлтый — внимание, красный — проблема.
 *
 * Живёт в общем слое, а не рядом с дашбордом: тот же набор цветов красит зоны и на
 * дашбордах, и в гриде диалогов, а импортировать друг друга страницы не могут.
 */
export type QualityZoneColor = 'green' | 'yellow' | 'red';

/**
 * Заливка зоны — единственный источник оттенка. Вариант `before:` для слоя ячейки —
 * {@link ZONE_CELL_FILL_CLASS}, править обе.
 *
 * Ступень `-50` выбрана вынужденно: `@ibsys/kit` переопределяет в палитре лишь часть шкалы,
 * и `yellow-100` у него — насыщенное золото, тогда как `green-100` и `red-100` остаются
 * пастельными от Tailwind. На `-50` все три цвета пастельные, поэтому тройка читается как
 * одна шкала.
 *
 * Семантические токены взять не из чего, хотя правило оформления требует именно их: заливки
 * есть только у `success` и `warning`, а сигнальный красный (`destructive`) задан насыщенным
 * и с белым текстом — светлой красной ступени в палитре нет. Собранная на них тройка развалилась
 * бы ровно так же, только на красном. Завести недостающую заливку нельзя правкой темы: перечень
 * цветов темы хранит бэкенд, и это отдельная задача — до неё зоны живут на палитре Tailwind.
 */
const ZONE_BG_CLASS: Record<QualityZoneColor, string> = {
  green: 'bg-green-50',
  yellow: 'bg-yellow-50',
  red: 'bg-red-50',
};

/**
 * Цвет текста зоны — парный к заливке и отдельно от неё.
 *
 * Отдельно, потому что цвет зоны несут и элементы без заливки: у дельты зона выражена
 * только цветом цифр. Оттенок при этом тот же — иначе зелёный текста и зелёный заливки
 * разойдутся ровно так, как раньше расходились жёлтые.
 */
export const zoneTextClass: Record<QualityZoneColor, string> = {
  green: 'text-green-900',
  yellow: 'text-yellow-900',
  red: 'text-red-900',
};

/** Классы рамки блока зоны — на ступень темнее заливки. */
const ZONE_BORDER_CLASS: Record<QualityZoneColor, string> = {
  green: 'border-green-200',
  yellow: 'border-yellow-200',
  red: 'border-red-200',
};

/** Классы бейджа зоны: заливка и текст без рамки. */
export const zoneBadgeClass: Record<QualityZoneColor, string> = {
  green: `${ZONE_BG_CLASS.green} ${zoneTextClass.green}`,
  yellow: `${ZONE_BG_CLASS.yellow} ${zoneTextClass.yellow}`,
  red: `${ZONE_BG_CLASS.red} ${zoneTextClass.red}`,
};

/** Классы блока зоны: заливка, текст и рамка. */
export const zoneCardClass: Record<QualityZoneColor, string> = {
  green: `${zoneBadgeClass.green} ${ZONE_BORDER_CLASS.green}`,
  yellow: `${zoneBadgeClass.yellow} ${ZONE_BORDER_CLASS.yellow}`,
  red: `${zoneBadgeClass.red} ${ZONE_BORDER_CLASS.red}`,
};

/**
 * Классы заливки бара, окрашенного по зоне.
 *
 * Бар берёт ступень рамки блока зоны, а не его заливки: пастельная `-50` на треке `bg-muted`
 * почти не видна, и протяжённость бара приходилось держать рамкой — сплошной цвет читается
 * лучше контура вокруг узкой полосы. Зона при этом узнаётся по тому же оттенку, что и везде.
 *
 * Вариант `fill:` для частей рисунка — {@link zoneChartFillClass}, править обе.
 */
export const zoneBarClass: Record<QualityZoneColor, string> = {
  green: 'bg-green-200',
  yellow: 'bg-yellow-200',
  red: 'bg-red-200',
};

/** Заливка зоны для части рисунка — вариант `fill:` карты {@link zoneBarClass}, править обе. */
export const zoneChartFillClass: Record<QualityZoneColor, string> = {
  green: 'fill-green-200',
  yellow: 'fill-yellow-200',
  red: 'fill-red-200',
};

/** Классы слоя-потомка, на котором нарисована заливка зоны в ячейке. */
const ZONE_FILL_LAYER_CLASS = 'relative z-0 before:absolute before:inset-y-0 before:inset-x-1 before:-z-10';

/** Заливка зоны для слоя ячейки — вариант `` карты {@link ZONE_BG_CLASS}, править обе. */
const ZONE_CELL_FILL_CLASS: Record<QualityZoneColor, string> = {
  green: 'bg-green-50',
  yellow: 'bg-yellow-50',
  red: 'bg-red-50',
};

/**
 * Классы ячейки таблицы, окрашенной по зоне: цвет текста и слой заливки под содержимым.
 *
 * `!` у цвета текста обязателен — собственные классы ячейки грида перебивают его без него;
 * ступень при этом повторяет {@link zoneTextClass} литералом, править обе.
 */
export const zoneCellClass: Record<QualityZoneColor, string> = {
  green: `font-medium text-green-900! ${ZONE_FILL_LAYER_CLASS} ${ZONE_CELL_FILL_CLASS.green}`,
  yellow: `font-medium text-yellow-900! ${ZONE_FILL_LAYER_CLASS} ${ZONE_CELL_FILL_CLASS.yellow}`,
  red: `font-medium text-red-900! ${ZONE_FILL_LAYER_CLASS} ${ZONE_CELL_FILL_CLASS.red}`,
};

/**
 * Пороги зон качества — нижние границы доли балла от максимума своей шкалы.
 *
 * `green` — с этой доли и выше зона зелёная; `yellow` — с этой доли и выше (но ниже
 * `green`) жёлтая; ниже `yellow` — красная. Обе доли в диапазоне (0, 1), `green` > `yellow`.
 */
export type QualityZoneRanges = {
  /** Нижняя граница доли балла от максимума для зелёной зоны. */
  green: number;
  /** Нижняя граница доли балла от максимума для жёлтой зоны; ниже неё — красная. */
  yellow: number;
};

/** Пороги зон качества по умолчанию: зелёная от 80 %, жёлтая от 60 % максимума шкалы. */
export const DEFAULT_ZONE_RANGES: QualityZoneRanges = { green: 0.8, yellow: 0.6 };

/**
 * Зона качества по доле балла от максимума своей шкалы.
 *
 * Границы по умолчанию живут здесь, а не у каждого потребителя: по ним относит оценку
 * к зоне бэкенд в сводке дашборда, и списку диалогов с дашбордом нужно попасть в ту же
 * зону, что и ему. Пользовательские пороги дашборда передаются вторым аргументом
 * (настройки отображения мини-отчёта).
 */
export const qualityZoneByRatio = (
  ratio: number,
  ranges: QualityZoneRanges = DEFAULT_ZONE_RANGES,
): QualityZoneColor => {
  if (ratio >= ranges.green) return 'green';
  if (ratio >= ranges.yellow) return 'yellow';
  return 'red';
};
